import Typography from "components/typography";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import LyricItemSync from "./lyricItemSync";
import SyncController from "./syncController";
import { FormatAudioDuration } from "utils/numberConvert";
import PrimaryButton from "components/buttons/primary-button";
import Modal from "components/modal";
import RestartResyncModal from "../modals/restartResyncModal";
import SavedLyricsModal from "../modals/savedLyricsModal";

interface IProps {
  lyrics: string;
  audio: string;
  isModalOpen: boolean;
  closeModal: () => void;
  modalItem: string;
  save: (lyrics: { text: string; timestamp: number }[]) => void;
  title: string;
  loading: boolean;
  syncedLyrics: { text: string; timestamp: number }[];
  isRestart: boolean;
}
const LyricsSync = ({
  lyrics,
  audio,
  isModalOpen,
  closeModal,
  modalItem,
  save,
  title,
  loading,
  syncedLyrics,
  isRestart
}: IProps) => {
  const EmptySync = [{ text: "#•••", timestamp: 0.01 }];
  const [formattedLyrics, setFormmatedLyrics] = useState<
    { text: string; timestamp: number }[]
  >(syncedLyrics?.length > 0 ? syncedLyrics : [...EmptySync]);

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [activeIndex, setActiveIndex] = useState(0);

  const [audioTime, setAudioTime] = useState(0);

  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const [freePlay, setFreePlay] = useState(true);

  const RestartSync = () => {
    const lines = lyrics.split("\n");
    const mappedLyrics = lines.map((line, index) => ({
      text: line.trim(),
      timestamp: 0
    }));
    setFormmatedLyrics([...EmptySync, ...mappedLyrics]);
    setActiveIndex(0);
    GoToTime(0);
    closeModal();
  };

  const SetTimestamp = (timestamp: number, index: number) => {
    setFormmatedLyrics((prevLyrics) =>
      prevLyrics.map((lyric, i) =>
        i === index ? { ...lyric, timestamp } : lyric
      )
    );
  };

  const SetText = (text: string, index: number) => {
    setFormmatedLyrics((prevLyrics) =>
      prevLyrics.map((lyric, i) => (i === index ? { ...lyric, text } : lyric))
    );
  };

  const scrollToItem = (index: number) => {
    if (itemRefs.current && itemRefs.current[index]) {
      itemRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  };

  //audio
  const GoToTime = (timestamp: number, play?: boolean) => {
    if (timestamp < 0 || timestamp > duration) {
      return;
    }
    setAudioTime(timestamp);
    if (audioRef.current) audioRef.current.currentTime = timestamp;
    if (audioRef.current) {
      setFreePlay(true);
      if (play) {
        audioRef.current.play();
        setIsPlaying(true);
      }
      const index = findIndexByTimestamp(audioTime);
      if (index) setActiveIndex(index);
    }
  };
  const findIndexByTimestamp = useCallback(
    (targetTimestamp: number) => {
      let closestIndex = -1;
      let closestDiff = Infinity;

      for (let i = 0; i < formattedLyrics.length; i++) {
        const activeTimestamp = formattedLyrics[i].timestamp;
        const diff = Math.abs(activeTimestamp - targetTimestamp);

        if (diff < closestDiff) {
          closestDiff = diff;
          closestIndex = i;
        }
      }

      return closestIndex;
    },
    [formattedLyrics]
  );

  const HandleSeek = (time: number) => {
    setAudioTime((Number(time) / 100) * duration);

    if (audioRef.current)
      audioRef.current.currentTime = (Number(time) / 100) * duration;

    const index = findIndexByTimestamp(audioTime);
    if (index) {
      setActiveIndex(index);
      setFreePlay(true);
    }
  };

  const handleLoadedMetadata = (event: any) => {
    const audioDuration = event.target.duration;
    setDuration(audioDuration);
  };

  const handleTimeUpdate = (event: any) => {
    const current = event.target.currentTime;

    setAudioTime(current);
  };
  const TogglePlay = () => {
    if (audioRef.current && !isPlaying) {
      audioRef.current.play();
      setIsPlaying(true);
      setFreePlay(true);
    }
    if (audioRef.current && isPlaying) {
      setIsPlaying(false);
      audioRef.current.pause();
    }
  };

  const Slowdown = (isSlow: boolean) => {
    if (!audioRef.current) {
      return;
    }
    if (!isSlow) {
      audioRef.current.playbackRate = 0.5;
    } else {
      audioRef.current.playbackRate = 1;
    }
  };

  useEffect(() => {
    if (isRestart) {
      RestartSync();
    }
  }, []);

  useEffect(() => {
    scrollToItem(activeIndex);
  }, [activeIndex]);

  useEffect(() => {
    if (audioTime < 0.01) {
      setActiveIndex(0);
    }

    if (freePlay) {
      const index = findIndexByTimestamp(audioTime);
      if (index) setActiveIndex(index);
    }
  }, [audioTime, findIndexByTimestamp, freePlay]);

  return (
    <Container>
      <Modal isActive={isModalOpen} closeModal={closeModal}>
        {modalItem === "restart" ? (
          <RestartResyncModal close={closeModal} restart={RestartSync} />
        ) : modalItem === "save" ? (
          <SavedLyricsModal close={closeModal} title={title} />
        ) : (
          <></>
        )}
      </Modal>

      <audio
        controls
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        style={{ display: "none" }}
        ref={audioRef}
      >
        <source src={audio} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      {formattedLyrics.map(({ text, timestamp }, i) => {
        const active = i === activeIndex;
        return (
          <Lyric
            key={i}
            isactive={active ? "true" : "false"}
            ref={(el) => (itemRefs.current[i] = el)}
          >
            {active && <ActiveBar />}
            <LyricMediaContainer>
              {timestamp !== 0 && (
                <LyricItemSync
                  timeStamp={timestamp}
                  setTimestamp={(value) => SetTimestamp(value, i)}
                  GoToTime={(value) => GoToTime(value)}
                />
              )}
            </LyricMediaContainer>
            {text[0] !== "#" ? (
              <LyricInput
                value={text}
                onChange={(e) => {
                  SetText(e.target.value, i);
                }}
                textcolor={active || timestamp !== 0 ? "white" : ""}
              />
            ) : (
              <GrayLyricContainer>
                <LyricInput
                  value={text.substring(1)}
                  onChange={(e) => {
                    SetText(`#${e.target.value}`, i);
                  }}
                  textcolor="white"
                />
              </GrayLyricContainer>
            )}
            {timestamp ? (
              <MobileTimeStampContainer>
                <Typography>{FormatAudioDuration(timestamp)}</Typography>
              </MobileTimeStampContainer>
            ) : (
              <></>
            )}
          </Lyric>
        );
      })}
      <SyncController
        save={() => save(formattedLyrics)}
        loading={loading}
        activeIndex={activeIndex}
        totalIndex={formattedLyrics.length - 1}
        setIndex={(i) => setActiveIndex(i)}
        currentTime={audioTime}
        handleSeek={(time) => HandleSeek(time)}
        setLyricTime={(time, index) => SetTimestamp(time, index)}
        togglePlay={TogglePlay}
        duration={duration}
        isPlaying={isPlaying}
        setFreePlay={(bool) => setFreePlay(bool)}
        GoToTime={(value) => GoToTime(value)}
        slowDown={(isSlow) => Slowdown(isSlow)}
      />
    </Container>
  );
};

export default LyricsSync;

const Container = styled.div`
  margin-top: 6rem;
  padding-bottom: 12rem;
`;

const Lyric = styled.div<{ isactive: string }>`
  width: 80%;
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;
  height: 4rem;
  background-color: ${(props) =>
    props.isactive === "true" ? "rgba(254, 183, 61, 0.1)" : "transparent"};

  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const LyricInput = styled.input<{ textcolor: string }>`
  border: none;
  outline: none;
  color: ${(props) =>
    props.textcolor === "white" ? "white" : "rgba(221, 221, 221, 0.2)"};
  width: 70%;

  font-size: 1.8rem;
  font-weight: 600;
  @media only screen and (max-width: 769px) {
    width: 95%;
  }
`;

const ActiveBar = styled.div`
  border-radius: 0px 8px 8px 0px;
  background-color: ${Colors.yellow500};
  width: 0.4rem;
  height: 100%;
  @media only screen and (max-width: 769px) {
    margin-right: 0.8rem;
  }
`;

const GrayLyricContainer = styled.div`
  border-radius: 2.4rem;
  background-color: ${Colors.grey600};
  padding: 0.8rem 1.2rem;
`;

const MobileTimeStampContainer = styled(GrayLyricContainer)`
  display: none;
  margin-left: auto;
  @media only screen and (max-width: 769px) {
    display: flex;
  }
`;
const LyricMediaContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 20rem;
  margin-left: 2.8rem;
  margin-right: 1.6rem;
  @media only screen and (max-width: 769px) {
    display: none;
  }
`;
